'use client';

import { useState } from 'react';
import { CloudCog, Code, Cpu, Database, Globe, Wrench } from 'lucide-react';
import { motion } from 'framer-motion';

export default function TechStack() {
  const [activeTechStack, setActiveTechStack] = useState<string>('ai-ml');
  const [isTransitioning, setIsTransitioning] = useState<boolean>(false);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 0,
      scale: 0.95,
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
    },
  };

  const handleTechStackClick = (techStack: string) => {
    if (techStack === activeTechStack || isTransitioning) return;

    setIsTransitioning(true);
    setActiveTechStack(techStack);

    setTimeout(() => {
      setIsTransitioning(false);
    }, 300);
  };

  const handleTechStackKeyDown = (
    event: React.KeyboardEvent,
    techStack: string,
  ) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleTechStackClick(techStack);
    }
  };

  return (
    <div className="relative">
      <div className="w-full">
        <div className="pt-20">
          <div className="pt-8">
            <motion.section
              id="tech-stack"
              className="relative scroll-mt-20 py-10"
              style={{
                transform: 'translateZ(0)',
                backfaceVisibility: 'hidden',
                perspective: '1000px',
              }}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, amount: 0.1 }}
              transition={{ duration: 1, ease: 'easeOut' }}
            >
              <div className="absolute inset-0"></div>
              <div
                className="relative z-10 mx-auto max-w-7xl px-4"
                style={{ willChange: 'auto' }}
              >
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  className="mb-16 text-center"
                >
                  <h2 className="bricolage mb-6 text-3xl font-extrabold sm:text-4xl md:text-5xl">
                    Technical <span className="text-primary">Stack</span>
                  </h2>
                  <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 1, delay: 0.5 }}
                    className="antonio text-muted-foreground mx-auto max-w-3xl text-xl sm:text-2xl"
                  >
                    Technologies and tools I use to build innovative solutions
                  </motion.p>
                </motion.div>
                <motion.div
                  className="mb-12"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.8, delay: 0.3 }}
                >
                  <div
                    className="flex flex-col justify-center space-y-4 px-4 lg:flex-row lg:space-y-0 lg:space-x-4 lg:px-0"
                    role="tablist"
                    aria-label="Technical stack categories"
                  >
                    <motion.button
                      onClick={() => handleTechStackClick('ai-ml')}
                      onKeyDown={e => handleTechStackKeyDown(e, 'ai-ml')}
                      className={`flex w-full cursor-pointer items-center gap-2 rounded-xl border px-4 py-2 transition-all duration-300 lg:w-auto ${
                        activeTechStack === 'ai-ml'
                          ? 'bg-primary text-primary-foreground border-primary shadow-lg'
                          : 'bg-card/50 text-muted-foreground border-secondary/20 hover:border-primary/50 hover:text-foreground'
                      }`}
                      tabIndex={0}
                      role="tab"
                      aria-selected={activeTechStack === 'ai-ml'}
                      whileHover={{
                        scale: 1.05,
                        y: -2,
                        transition: { duration: 0.2, ease: 'easeOut' },
                      }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Cpu className="h-5 w-5" />
                      <span className="text-sm font-medium">
                        AI &amp; Machine Learning
                      </span>
                    </motion.button>
                    <button
                      onClick={() => handleTechStackClick('frontend')}
                      onKeyDown={e => handleTechStackKeyDown(e, 'frontend')}
                      className={`flex w-full cursor-pointer items-center gap-2 rounded-xl border px-4 py-2 transition-all duration-300 lg:w-auto ${
                        activeTechStack === 'frontend'
                          ? 'bg-primary text-primary-foreground border-primary shadow-lg'
                          : 'bg-card/50 text-muted-foreground border-secondary/20 hover:border-primary/50 hover:text-foreground'
                      }`}
                      tabIndex={0}
                      role="tab"
                      aria-selected={activeTechStack === 'frontend'}
                    >
                      <Code className="h-5 w-5" />
                      <span className="text-sm font-medium">
                        Frontend Development
                      </span>
                    </button>
                    <button
                      onClick={() => handleTechStackClick('backend')}
                      onKeyDown={e => handleTechStackKeyDown(e, 'backend')}
                      className={`flex w-full cursor-pointer items-center gap-2 rounded-xl border px-4 py-2 transition-all duration-300 lg:w-auto ${
                        activeTechStack === 'backend'
                          ? 'bg-primary text-primary-foreground border-primary shadow-lg'
                          : 'bg-card/50 text-muted-foreground border-secondary/20 hover:border-primary/50 hover:text-foreground'
                      }`}
                      tabIndex={0}
                      role="tab"
                      aria-selected={activeTechStack === 'backend'}
                    >
                      <Database className="h-5 w-5" />
                      <span className="text-sm font-medium">
                        Backend &amp; APIs
                      </span>
                    </button>
                    <button
                      onClick={() => handleTechStackClick('cloud')}
                      onKeyDown={e => handleTechStackKeyDown(e, 'cloud')}
                      className={`flex w-full cursor-pointer items-center gap-2 rounded-xl border px-4 py-2 transition-all duration-300 lg:w-auto ${
                        activeTechStack === 'cloud'
                          ? 'bg-primary text-primary-foreground border-primary shadow-lg'
                          : 'bg-card/50 text-muted-foreground border-secondary/20 hover:border-primary/50 hover:text-foreground'
                      }`}
                      tabIndex={0}
                      role="tab"
                      aria-selected={activeTechStack === 'cloud'}
                    >
                      <CloudCog className="h-5 w-5" />
                      <span className="text-sm font-medium">
                        Cloud &amp; DevOps
                      </span>
                    </button>
                    <button
                      onClick={() => handleTechStackClick('tools')}
                      onKeyDown={e => handleTechStackKeyDown(e, 'tools')}
                      className={`flex w-full cursor-pointer items-center gap-2 rounded-xl border px-4 py-2 transition-all duration-300 lg:w-auto ${
                        activeTechStack === 'tools'
                          ? 'bg-primary text-primary-foreground border-primary shadow-lg'
                          : 'bg-card/50 text-muted-foreground border-secondary/20 hover:border-primary/50 hover:text-foreground'
                      }`}
                      tabIndex={0}
                      role="tab"
                      aria-selected={activeTechStack === 'tools'}
                      style={{ transform: 'none' }}
                    >
                      <Wrench className="h-5 w-5" />
                      <span className="text-sm font-medium">
                        Development Tools
                      </span>
                    </button>
                    <button
                      onClick={() => handleTechStackClick('web3')}
                      onKeyDown={e => handleTechStackKeyDown(e, 'web3')}
                      className={`flex w-full cursor-pointer items-center gap-2 rounded-xl border px-4 py-2 transition-all duration-300 lg:w-auto ${
                        activeTechStack === 'web3'
                          ? 'bg-primary text-primary-foreground border-primary shadow-lg'
                          : 'bg-card/50 text-muted-foreground border-secondary/20 hover:border-primary/50 hover:text-foreground'
                      }`}
                      tabIndex={0}
                      role="tab"
                      aria-selected={activeTechStack === 'web3'}
                    >
                      <Globe className="h-5 w-5" />
                      <span className="text-sm font-medium">
                        Web3 &amp; Blockchain
                      </span>
                    </button>
                  </div>
                </motion.div>
                <motion.div
                  key={activeTechStack}
                  className="tech-stack-content grid grid-cols-1 gap-6 transition-all duration-300 md:grid-cols-2 lg:grid-cols-3"
                  style={{
                    transform: 'translateZ(0)',
                    backfaceVisibility: 'hidden',
                    willChange: 'auto',
                  }}
                  variants={containerVariants}
                  initial="hidden"
                  whileInView="visible"
                  exit="exit"
                  transition={{
                    duration: 0.4,
                    ease: [0.25, 0.46, 0.45, 0.94],
                  }}
                >
                  {activeTechStack === 'ai-ml' && (
                    <>
                      <motion.div
                        variants={cardVariants}
                        className="bg-card/80 border-secondary/20 group hover:border-primary/30 relative rounded-xl border p-6 transition-all duration-300 hover:shadow-lg"
                        whileHover={{
                          transition: { duration: 0.3, ease: 'easeOut' },
                        }}
                      >
                        <div className="mb-4 flex items-center justify-between">
                          <div className="text-foreground group-hover:text-primary text-lg font-bold transition-colors">
                            Python
                          </div>
                          <span className="text-muted-foreground text-sm">
                            50 projects
                          </span>
                        </div>
                        <div className="mb-4">
                          <div className="mb-2 flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              Proficiency
                            </span>
                            <span className="text-secondary font-semibold">
                              95%
                            </span>
                          </div>
                          <div className="bg-muted h-2 w-full overflow-hidden rounded-full">
                            <motion.div
                              className="h-2 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500"
                              initial={{ width: 0 }}
                              animate={{ width: '95%' }}
                              transition={{
                                duration: 1.5,
                                delay: 0.2,
                                ease: 'easeOut',
                              }}
                            ></motion.div>
                          </div>
                        </div>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                          Primary language for AI/ML development
                        </p>
                        <div className="absolute top-4 right-4 h-2 w-2 rounded-full bg-cyan-500 opacity-50 transition-opacity group-hover:opacity-100"></div>
                      </motion.div>
                      <motion.div
                        variants={cardVariants}
                        className="bg-card/80 border-secondary/20 group hover:border-primary/30 relative rounded-xl border p-6 transition-all duration-300 hover:shadow-lg"
                        whileHover={{
                          transition: { duration: 0.3, ease: 'easeOut' },
                        }}
                      >
                        <div className="mb-4 flex items-center justify-between">
                          <div className="text-foreground group-hover:text-primary text-lg font-bold transition-colors">
                            TensorFlow
                          </div>
                          <span className="text-muted-foreground text-sm">
                            25 projects
                          </span>
                        </div>
                        <div className="mb-4">
                          <div className="mb-2 flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              Proficiency
                            </span>
                            <span className="text-secondary font-semibold">
                              90%
                            </span>
                          </div>
                          <div className="bg-muted h-2 w-full overflow-hidden rounded-full">
                            <motion.div
                              className="h-2 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500"
                              initial={{ width: 0 }}
                              animate={{ width: '90%' }}
                              transition={{
                                duration: 1.5,
                                delay: 0.4,
                                ease: 'easeOut',
                              }}
                            ></motion.div>
                          </div>
                        </div>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                          Deep learning framework
                        </p>
                        <div className="absolute top-4 right-4 h-2 w-2 rounded-full bg-cyan-500 opacity-50 transition-opacity group-hover:opacity-100"></div>
                      </motion.div>
                      <motion.div
                        variants={cardVariants}
                        className="bg-card/80 border-secondary/20 group hover:border-primary/30 relative rounded-xl border p-6 transition-all duration-300 hover:shadow-lg"
                        whileHover={{
                          transition: { duration: 0.3, ease: 'easeOut' },
                        }}
                      >
                        <div className="mb-4 flex items-center justify-between">
                          <div className="text-foreground group-hover:text-primary text-lg font-bold transition-colors">
                            PyTorch
                          </div>
                          <span className="text-muted-foreground text-sm">
                            20 projects
                          </span>
                        </div>
                        <div className="mb-4">
                          <div className="mb-2 flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              Proficiency
                            </span>
                            <span className="text-secondary font-semibold">
                              88%
                            </span>
                          </div>
                          <div className="bg-muted h-2 w-full overflow-hidden rounded-full">
                            <motion.div
                              className="h-2 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500"
                              initial={{ width: 0 }}
                              animate={{ width: '88%' }}
                              transition={{
                                duration: 1.5,
                                delay: 0.6,
                                ease: 'easeOut',
                              }}
                            ></motion.div>
                          </div>
                        </div>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                          Research-focused ML framework
                        </p>
                        <div className="absolute top-4 right-4 h-2 w-2 rounded-full bg-cyan-500 opacity-50 transition-opacity group-hover:opacity-100"></div>
                      </motion.div>
                      <motion.div
                        variants={cardVariants}
                        className="bg-card/80 border-secondary/20 group hover:border-primary/30 relative rounded-xl border p-6 transition-all duration-300 hover:shadow-lg"
                        whileHover={{
                          transition: { duration: 0.3, ease: 'easeOut' },
                        }}
                      >
                        <div className="mb-4 flex items-center justify-between">
                          <div className="text-foreground group-hover:text-primary text-lg font-bold transition-colors">
                            Hugging Face
                          </div>
                          <span className="text-muted-foreground text-sm">
                            30 projects
                          </span>
                        </div>
                        <div className="mb-4">
                          <div className="mb-2 flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              Proficiency
                            </span>
                            <span className="text-secondary font-semibold">
                              92%
                            </span>
                          </div>
                          <div className="bg-muted h-2 w-full overflow-hidden rounded-full">
                            <motion.div
                              className="h-2 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500"
                              initial={{ width: 0 }}
                              animate={{ width: '92%' }}
                              transition={{
                                duration: 1.5,
                                delay: 0.8,
                                ease: 'easeOut',
                              }}
                            ></motion.div>
                          </div>
                        </div>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                          NLP and transformer models
                        </p>
                        <div className="absolute top-4 right-4 h-2 w-2 rounded-full bg-cyan-500 opacity-50 transition-opacity group-hover:opacity-100"></div>
                      </motion.div>
                      <motion.div
                        variants={cardVariants}
                        className="bg-card/80 border-secondary/20 group hover:border-primary/30 relative rounded-xl border p-6 transition-all duration-300 hover:shadow-lg"
                        whileHover={{
                          transition: { duration: 0.3, ease: 'easeOut' },
                        }}
                      >
                        <div className="mb-4 flex items-center justify-between">
                          <div className="text-foreground group-hover:text-primary text-lg font-bold transition-colors">
                            LangChain
                          </div>
                          <span className="text-muted-foreground text-sm">
                            15 projects
                          </span>
                        </div>
                        <div className="mb-4">
                          <div className="mb-2 flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              Proficiency
                            </span>
                            <span className="text-secondary font-semibold">
                              85%
                            </span>
                          </div>
                          <div className="bg-muted h-2 w-full overflow-hidden rounded-full">
                            <motion.div
                              className="h-2 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500"
                              initial={{ width: 0 }}
                              animate={{ width: '85%' }}
                              transition={{
                                duration: 1.5,
                                delay: 1,
                                ease: 'easeOut',
                              }}
                            ></motion.div>
                          </div>
                        </div>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                          LLM application framework
                        </p>
                        <div className="absolute top-4 right-4 h-2 w-2 rounded-full bg-cyan-500 opacity-50 transition-opacity group-hover:opacity-100"></div>
                      </motion.div>
                      <motion.div
                        variants={cardVariants}
                        className="bg-card/80 border-secondary/20 group hover:border-primary/30 relative rounded-xl border p-6 transition-all duration-300 hover:shadow-lg"
                        whileHover={{
                          transition: { duration: 0.3, ease: 'easeOut' },
                        }}
                      >
                        <div className="mb-4 flex items-center justify-between">
                          <div className="text-foreground group-hover:text-primary text-lg font-bold transition-colors">
                            OpenAI API
                          </div>
                          <span className="text-muted-foreground text-sm">
                            35 projects
                          </span>
                        </div>
                        <div className="mb-4">
                          <div className="mb-2 flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              Proficiency
                            </span>
                            <span className="text-secondary font-semibold">
                              90%
                            </span>
                          </div>
                          <div className="bg-muted h-2 w-full overflow-hidden rounded-full">
                            <motion.div
                              className="h-2 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500"
                              initial={{ width: 0 }}
                              animate={{ width: '90%' }}
                              transition={{
                                duration: 1.5,
                                delay: 1.2,
                                ease: 'easeOut',
                              }}
                            ></motion.div>
                          </div>
                        </div>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                          GPT integration and fine-tuning
                        </p>
                        <div className="absolute top-4 right-4 h-2 w-2 rounded-full bg-cyan-500 opacity-50 transition-opacity group-hover:opacity-100"></div>
                      </motion.div>
                    </>
                  )}

                  {activeTechStack === 'frontend' && (
                    <>
                      <motion.div
                        variants={cardVariants}
                        className="bg-card/80 border-secondary/20 group hover:border-primary/30 relative rounded-xl border p-6 transition-all duration-300 hover:shadow-lg"
                        whileHover={{
                          transition: { duration: 0.3, ease: 'easeOut' },
                        }}
                      >
                        <div className="mb-4 flex items-center justify-between">
                          <div className="text-foreground group-hover:text-primary text-lg font-bold transition-colors">
                            React & Next.js
                          </div>
                          <span className="text-muted-foreground text-sm">
                            40 projects
                          </span>
                        </div>
                        <div className="mb-4">
                          <div className="mb-2 flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              Proficiency
                            </span>
                            <span className="text-secondary font-semibold">
                              95%
                            </span>
                          </div>
                          <div className="bg-muted h-2 w-full rounded-full">
                            <motion.div
                              className="from-elementTwo to-primary h-2 rounded-full bg-gradient-to-r"
                              style={{ width: '95%' }}
                              initial={{ width: 0 }}
                              animate={{ width: '95%' }}
                              transition={{
                                duration: 1.5,
                                delay: 0.2,
                                ease: 'easeOut',
                              }}
                            ></motion.div>
                          </div>
                        </div>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                          Modern React with hooks, context, and Next.js SSR/SSG
                        </p>
                        <div className="absolute top-4 right-4 h-2 w-2 rounded-full bg-blue-500 opacity-50 transition-opacity group-hover:opacity-100"></div>
                      </motion.div>
                      <motion.div
                        variants={cardVariants}
                        className="bg-card/80 border-secondary/20 group hover:border-primary/30 relative rounded-xl border p-6 transition-all duration-300 hover:shadow-lg"
                        whileHover={{
                          transition: { duration: 0.3, ease: 'easeOut' },
                        }}
                      >
                        <div className="mb-4 flex items-center justify-between">
                          <div className="text-foreground group-hover:text-primary text-lg font-bold transition-colors">
                            TypeScript
                          </div>
                          <span className="text-muted-foreground text-sm">
                            35 projects
                          </span>
                        </div>
                        <div className="mb-4">
                          <div className="mb-2 flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              Proficiency
                            </span>
                            <span className="text-secondary font-semibold">
                              90%
                            </span>
                          </div>
                          <div className="bg-muted h-2 w-full rounded-full">
                            <motion.div
                              className="from-elementTwo to-primary h-2 rounded-full bg-gradient-to-r"
                              style={{ width: '90%' }}
                              initial={{ width: 0 }}
                              animate={{ width: '90%' }}
                              transition={{
                                duration: 1.5,
                                delay: 0.4,
                                ease: 'easeOut',
                              }}
                            ></motion.div>
                          </div>
                        </div>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                          Type-safe development with advanced type patterns
                        </p>
                        <div className="absolute top-4 right-4 h-2 w-2 rounded-full bg-blue-500 opacity-50 transition-opacity group-hover:opacity-100"></div>
                      </motion.div>
                      <motion.div
                        variants={cardVariants}
                        className="bg-card/80 border-secondary/20 group hover:border-primary/30 relative rounded-xl border p-6 transition-all duration-300 hover:shadow-lg"
                        whileHover={{
                          transition: { duration: 0.3, ease: 'easeOut' },
                        }}
                      >
                        <div className="mb-4 flex items-center justify-between">
                          <div className="text-foreground group-hover:text-primary text-lg font-bold transition-colors">
                            Tailwind CSS
                          </div>
                          <span className="text-muted-foreground text-sm">
                            30 projects
                          </span>
                        </div>
                        <div className="mb-4">
                          <div className="mb-2 flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              Proficiency
                            </span>
                            <span className="text-secondary font-semibold">
                              92%
                            </span>
                          </div>
                          <div className="bg-muted h-2 w-full rounded-full">
                            <motion.div
                              className="from-elementTwo to-primary h-2 rounded-full bg-gradient-to-r"
                              style={{ width: '92%' }}
                              initial={{ width: 0 }}
                              animate={{ width: '92%' }}
                              transition={{
                                duration: 1.5,
                                delay: 0.6,
                                ease: 'easeOut',
                              }}
                            ></motion.div>
                          </div>
                        </div>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                          Utility-first CSS framework for rapid UI development
                        </p>
                        <div className="absolute top-4 right-4 h-2 w-2 rounded-full bg-blue-500 opacity-50 transition-opacity group-hover:opacity-100"></div>
                      </motion.div>
                      <motion.div
                        variants={cardVariants}
                        className="bg-card/80 border-secondary/20 group hover:border-primary/30 relative rounded-xl border p-6 transition-all duration-300 hover:shadow-lg"
                        whileHover={{
                          transition: { duration: 0.3, ease: 'easeOut' },
                        }}
                      >
                        <div className="mb-4 flex items-center justify-between">
                          <div className="text-foreground group-hover:text-primary text-lg font-bold transition-colors">
                            Framer Motion
                          </div>
                          <span className="text-muted-foreground text-sm">
                            25 projects
                          </span>
                        </div>
                        <div className="mb-4">
                          <div className="mb-2 flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              Proficiency
                            </span>
                            <span className="text-secondary font-semibold">
                              88%
                            </span>
                          </div>
                          <div className="bg-muted h-2 w-full rounded-full">
                            <motion.div
                              className="from-elementTwo to-primary h-2 rounded-full bg-gradient-to-r"
                              style={{ width: '88%' }}
                              initial={{ width: 0 }}
                              animate={{ width: '88%' }}
                              transition={{
                                duration: 1.5,
                                delay: 0.8,
                                ease: 'easeOut',
                              }}
                            ></motion.div>
                          </div>
                        </div>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                          Advanced animations and micro-interactions
                        </p>
                        <div className="absolute top-4 right-4 h-2 w-2 rounded-full bg-blue-500 opacity-50 transition-opacity group-hover:opacity-100"></div>
                      </motion.div>
                      <motion.div
                        variants={cardVariants}
                        className="bg-card/80 border-secondary/20 group hover:border-primary/30 relative rounded-xl border p-6 transition-all duration-300 hover:shadow-lg"
                        whileHover={{
                          transition: { duration: 0.3, ease: 'easeOut' },
                        }}
                      >
                        <div className="mb-4 flex items-center justify-between">
                          <div className="text-foreground group-hover:text-primary text-lg font-bold transition-colors">
                            GSAP
                          </div>
                          <span className="text-muted-foreground text-sm">
                            20 projects
                          </span>
                        </div>
                        <div className="mb-4">
                          <div className="mb-2 flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              Proficiency
                            </span>
                            <span className="text-secondary font-semibold">
                              85%
                            </span>
                          </div>
                          <div className="bg-muted h-2 w-full rounded-full">
                            <motion.div
                              className="from-elementTwo to-primary h-2 rounded-full bg-gradient-to-r"
                              style={{ width: '85%' }}
                              initial={{ width: 0 }}
                              animate={{ width: '85%' }}
                              transition={{
                                duration: 1.5,
                                delay: 1.0,
                                ease: 'easeOut',
                              }}
                            ></motion.div>
                          </div>
                        </div>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                          Professional-grade animations and scroll triggers
                        </p>
                        <div className="absolute top-4 right-4 h-2 w-2 rounded-full bg-blue-500 opacity-50 transition-opacity group-hover:opacity-100"></div>
                      </motion.div>
                      <motion.div
                        variants={cardVariants}
                        className="bg-card/80 border-secondary/20 group hover:border-primary/30 relative rounded-xl border p-6 transition-all duration-300 hover:shadow-lg"
                        whileHover={{
                          transition: { duration: 0.3, ease: 'easeOut' },
                        }}
                      >
                        <div className="mb-4 flex items-center justify-between">
                          <div className="text-foreground group-hover:text-primary text-lg font-bold transition-colors">
                            Three.js
                          </div>
                          <span className="text-muted-foreground text-sm">
                            15 projects
                          </span>
                        </div>
                        <div className="mb-4">
                          <div className="mb-2 flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              Proficiency
                            </span>
                            <span className="text-secondary font-semibold">
                              80%
                            </span>
                          </div>
                          <div className="bg-muted h-2 w-full rounded-full">
                            <motion.div
                              className="from-elementTwo to-primary h-2 rounded-full bg-gradient-to-r"
                              style={{ width: '80%' }}
                              initial={{ width: 0 }}
                              animate={{ width: '80%' }}
                              transition={{
                                duration: 1.5,
                                delay: 1.2,
                                ease: 'easeOut',
                              }}
                            ></motion.div>
                          </div>
                        </div>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                          3D graphics and interactive web experiences
                        </p>
                        <div className="absolute top-4 right-4 h-2 w-2 rounded-full bg-blue-500 opacity-50 transition-opacity group-hover:opacity-100"></div>
                      </motion.div>
                    </>
                  )}

                  {activeTechStack === 'backend' && (
                    <>
                      <motion.div
                        variants={cardVariants}
                        className="bg-card/80 border-secondary/20 group hover:border-primary/30 relative rounded-xl border p-6 transition-all duration-300 hover:shadow-lg"
                        whileHover={{
                          transition: { duration: 0.3, ease: 'easeOut' },
                        }}
                      >
                        <div className="mb-4 flex items-center justify-between">
                          <div className="text-foreground group-hover:text-primary text-lg font-bold transition-colors">
                            Node.js & Express
                          </div>
                          <span className="text-muted-foreground text-sm">
                            30 projects
                          </span>
                        </div>
                        <div className="mb-4">
                          <div className="mb-2 flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              Proficiency
                            </span>
                            <span className="text-secondary font-semibold">
                              92%
                            </span>
                          </div>
                          <div className="bg-muted h-2 w-full rounded-full">
                            <motion.div
                              className="h-2 rounded-full bg-gradient-to-r from-green-500 to-emerald-500"
                              style={{ width: '92%' }}
                              initial={{ width: 0 }}
                              animate={{ width: '92%' }}
                              transition={{
                                duration: 1.5,
                                delay: 0.2,
                                ease: 'easeOut',
                              }}
                            ></motion.div>
                          </div>
                        </div>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                          Server-side JavaScript with RESTful APIs
                        </p>
                        <div className="absolute top-4 right-4 h-2 w-2 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 opacity-50 transition-opacity group-hover:opacity-100"></div>
                      </motion.div>
                      <motion.div
                        variants={cardVariants}
                        className="bg-card/80 border-secondary/20 group hover:border-primary/30 relative rounded-xl border p-6 transition-all duration-300 hover:shadow-lg"
                        whileHover={{
                          transition: { duration: 0.3, ease: 'easeOut' },
                        }}
                      >
                        <div className="mb-4 flex items-center justify-between">
                          <div className="text-foreground group-hover:text-primary text-lg font-bold transition-colors">
                            Python & FastAPI
                          </div>
                          <span className="text-muted-foreground text-sm">
                            25 projects
                          </span>
                        </div>
                        <div className="mb-4">
                          <div className="mb-2 flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              Proficiency
                            </span>
                            <span className="text-secondary font-semibold">
                              88%
                            </span>
                          </div>
                          <div className="bg-muted h-2 w-full rounded-full">
                            <motion.div
                              className="h-2 rounded-full bg-gradient-to-r from-green-500 to-emerald-500"
                              style={{ width: '88%' }}
                              initial={{ width: 0 }}
                              animate={{ width: '88%' }}
                              transition={{
                                duration: 1.5,
                                delay: 0.4,
                                ease: 'easeOut',
                              }}
                            ></motion.div>
                          </div>
                        </div>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                          High-performance async Python web framework
                        </p>
                        <div className="absolute top-4 right-4 h-2 w-2 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 opacity-50 transition-opacity group-hover:opacity-100"></div>
                      </motion.div>
                      <motion.div
                        variants={cardVariants}
                        className="bg-card/80 border-secondary/20 group hover:border-primary/30 relative rounded-xl border p-6 transition-all duration-300 hover:shadow-lg"
                        whileHover={{
                          transition: { duration: 0.3, ease: 'easeOut' },
                        }}
                      >
                        <div className="mb-4 flex items-center justify-between">
                          <div className="text-foreground group-hover:text-primary text-lg font-bold transition-colors">
                            PostgreSQL
                          </div>
                          <span className="text-muted-foreground text-sm">
                            20 projects
                          </span>
                        </div>
                        <div className="mb-4">
                          <div className="mb-2 flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              Proficiency
                            </span>
                            <span className="text-secondary font-semibold">
                              85%
                            </span>
                          </div>
                          <div className="bg-muted h-2 w-full rounded-full">
                            <motion.div
                              className="h-2 rounded-full bg-gradient-to-r from-green-500 to-emerald-500"
                              style={{ width: '85%' }}
                              initial={{ width: 0 }}
                              animate={{ width: '85%' }}
                              transition={{
                                duration: 1.5,
                                delay: 0.6,
                                ease: 'easeOut',
                              }}
                            ></motion.div>
                          </div>
                        </div>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                          Advanced relational database management
                        </p>
                        <div className="absolute top-4 right-4 h-2 w-2 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 opacity-50 transition-opacity group-hover:opacity-100"></div>
                      </motion.div>
                      <motion.div
                        variants={cardVariants}
                        className="bg-card/80 border-secondary/20 group hover:border-primary/30 relative rounded-xl border p-6 transition-all duration-300 hover:shadow-lg"
                        whileHover={{
                          transition: { duration: 0.3, ease: 'easeOut' },
                        }}
                      >
                        <div className="mb-4 flex items-center justify-between">
                          <div className="text-foreground group-hover:text-primary text-lg font-bold transition-colors">
                            MongoDB
                          </div>
                          <span className="text-muted-foreground text-sm">
                            18 projects
                          </span>
                        </div>
                        <div className="mb-4">
                          <div className="mb-2 flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              Proficiency
                            </span>
                            <span className="text-secondary font-semibold">
                              82%
                            </span>
                          </div>
                          <div className="bg-muted h-2 w-full rounded-full">
                            <motion.div
                              className="h-2 rounded-full bg-gradient-to-r from-green-500 to-emerald-500"
                              style={{ width: '82%' }}
                              initial={{ width: 0 }}
                              animate={{ width: '82%' }}
                              transition={{
                                duration: 1.5,
                                delay: 0.8,
                                ease: 'easeOut',
                              }}
                            ></motion.div>
                          </div>
                        </div>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                          NoSQL document database with aggregation
                        </p>
                        <div className="absolute top-4 right-4 h-2 w-2 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 opacity-50 transition-opacity group-hover:opacity-100"></div>
                      </motion.div>
                      <motion.div
                        variants={cardVariants}
                        className="bg-card/80 border-secondary/20 group hover:border-primary/30 relative rounded-xl border p-6 transition-all duration-300 hover:shadow-lg"
                        whileHover={{
                          transition: { duration: 0.3, ease: 'easeOut' },
                        }}
                      >
                        <div className="mb-4 flex items-center justify-between">
                          <div className="text-foreground group-hover:text-primary text-lg font-bold transition-colors">
                            Redis
                          </div>
                          <span className="text-muted-foreground text-sm">
                            15 projects
                          </span>
                        </div>
                        <div className="mb-4">
                          <div className="mb-2 flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              Proficiency
                            </span>
                            <span className="text-secondary font-semibold">
                              80%
                            </span>
                          </div>
                          <div className="bg-muted h-2 w-full rounded-full">
                            <motion.div
                              className="h-2 rounded-full bg-gradient-to-r from-green-500 to-emerald-500"
                              style={{ width: '80%' }}
                              initial={{ width: 0 }}
                              animate={{ width: '80%' }}
                              transition={{
                                duration: 1.5,
                                delay: 1.0,
                                ease: 'easeOut',
                              }}
                            ></motion.div>
                          </div>
                        </div>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                          In-memory data structure store and cache
                        </p>
                        <div className="absolute top-4 right-4 h-2 w-2 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 opacity-50 transition-opacity group-hover:opacity-100"></div>
                      </motion.div>
                      <motion.div
                        variants={cardVariants}
                        className="bg-card/80 border-secondary/20 group hover:border-primary/30 relative rounded-xl border p-6 transition-all duration-300 hover:shadow-lg"
                        whileHover={{
                          transition: { duration: 0.3, ease: 'easeOut' },
                        }}
                      >
                        <div className="mb-4 flex items-center justify-between">
                          <div className="text-foreground group-hover:text-primary text-lg font-bold transition-colors">
                            Docker
                          </div>
                          <span className="text-muted-foreground text-sm">
                            22 projects
                          </span>
                        </div>
                        <div className="mb-4">
                          <div className="mb-2 flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              Proficiency
                            </span>
                            <span className="text-secondary font-semibold">
                              85%
                            </span>
                          </div>
                          <div className="bg-muted h-2 w-full rounded-full">
                            <motion.div
                              className="h-2 rounded-full bg-gradient-to-r from-green-500 to-emerald-500"
                              style={{ width: '85%' }}
                              initial={{ width: 0 }}
                              animate={{ width: '85%' }}
                              transition={{
                                duration: 1.5,
                                delay: 1.2,
                                ease: 'easeOut',
                              }}
                            ></motion.div>
                          </div>
                        </div>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                          Containerization and deployment automation
                        </p>
                        <div className="absolute top-4 right-4 h-2 w-2 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 opacity-50 transition-opacity group-hover:opacity-100"></div>
                      </motion.div>
                    </>
                  )}

                  {activeTechStack === 'cloud' && (
                    <>
                      <motion.div
                        variants={cardVariants}
                        className="bg-card/80 border-secondary/20 group hover:border-primary/30 relative rounded-xl border p-6 transition-all duration-300 hover:shadow-lg"
                        whileHover={{
                          transition: { duration: 0.3, ease: 'easeOut' },
                        }}
                      >
                        <div className="mb-4 flex items-center justify-between">
                          <div className="text-foreground group-hover:text-primary text-lg font-bold transition-colors">
                            AWS
                          </div>
                          <span className="text-muted-foreground text-sm">
                            25 projects
                          </span>
                        </div>
                        <div className="mb-4">
                          <div className="mb-2 flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              Proficiency
                            </span>
                            <span className="text-secondary font-semibold">
                              88%
                            </span>
                          </div>
                          <div className="bg-muted h-2 w-full rounded-full">
                            <motion.div
                              className="h-2 rounded-full bg-gradient-to-r from-orange-500 to-yellow-500"
                              style={{ width: '88%' }}
                              initial={{ width: 0 }}
                              animate={{ width: '88%' }}
                              transition={{
                                duration: 1.5,
                                delay: 0.2,
                                ease: 'easeOut',
                              }}
                            ></motion.div>
                          </div>
                        </div>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                          Cloud infrastructure and serverless architecture
                        </p>
                        <div className="absolute top-4 right-4 h-2 w-2 rounded-full bg-gradient-to-r from-orange-500 to-yellow-500 opacity-50 transition-opacity group-hover:opacity-100"></div>
                      </motion.div>
                      <motion.div
                        variants={cardVariants}
                        className="bg-card/80 border-secondary/20 group hover:border-primary/30 relative rounded-xl border p-6 transition-all duration-300 hover:shadow-lg"
                        whileHover={{
                          transition: { duration: 0.3, ease: 'easeOut' },
                        }}
                      >
                        <div className="mb-4 flex items-center justify-between">
                          <div className="text-foreground group-hover:text-primary text-lg font-bold transition-colors">
                            Vercel
                          </div>
                          <span className="text-muted-foreground text-sm">
                            30 projects
                          </span>
                        </div>
                        <div className="mb-4">
                          <div className="mb-2 flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              Proficiency
                            </span>
                            <span className="text-secondary font-semibold">
                              92%
                            </span>
                          </div>
                          <div className="bg-muted h-2 w-full rounded-full">
                            <motion.div
                              className="h-2 rounded-full bg-gradient-to-r from-orange-500 to-yellow-500"
                              style={{ width: '92%' }}
                              initial={{ width: 0 }}
                              animate={{ width: '92%' }}
                              transition={{
                                duration: 1.5,
                                delay: 0.4,
                                ease: 'easeOut',
                              }}
                            ></motion.div>
                          </div>
                        </div>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                          Next.js deployment and edge functions
                        </p>
                        <div className="absolute top-4 right-4 h-2 w-2 rounded-full bg-gradient-to-r from-orange-500 to-yellow-500 opacity-50 transition-opacity group-hover:opacity-100"></div>
                      </motion.div>
                      <motion.div
                        variants={cardVariants}
                        className="bg-card/80 border-secondary/20 group hover:border-primary/30 relative rounded-xl border p-6 transition-all duration-300 hover:shadow-lg"
                        whileHover={{
                          transition: { duration: 0.3, ease: 'easeOut' },
                        }}
                      >
                        <div className="mb-4 flex items-center justify-between">
                          <div className="text-foreground group-hover:text-primary text-lg font-bold transition-colors">
                            Netlify
                          </div>
                          <span className="text-muted-foreground text-sm">
                            20 projects
                          </span>
                        </div>
                        <div className="mb-4">
                          <div className="mb-2 flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              Proficiency
                            </span>
                            <span className="text-secondary font-semibold">
                              85%
                            </span>
                          </div>
                          <div className="bg-muted h-2 w-full rounded-full">
                            <motion.div
                              className="h-2 rounded-full bg-gradient-to-r from-orange-500 to-yellow-500"
                              style={{ width: '85%' }}
                              initial={{ width: 0 }}
                              animate={{ width: '85%' }}
                              transition={{
                                duration: 1.5,
                                delay: 0.6,
                                ease: 'easeOut',
                              }}
                            ></motion.div>
                          </div>
                        </div>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                          Static site hosting and form handling
                        </p>
                        <div className="absolute top-4 right-4 h-2 w-2 rounded-full bg-gradient-to-r from-orange-500 to-yellow-500 opacity-50 transition-opacity group-hover:opacity-100"></div>
                      </motion.div>
                      <motion.div
                        variants={cardVariants}
                        className="bg-card/80 border-secondary/20 group hover:border-primary/30 relative rounded-xl border p-6 transition-all duration-300 hover:shadow-lg"
                        whileHover={{
                          transition: { duration: 0.3, ease: 'easeOut' },
                        }}
                      >
                        <div className="mb-4 flex items-center justify-between">
                          <div className="text-foreground group-hover:text-primary text-lg font-bold transition-colors">
                            Railway
                          </div>
                          <span className="text-muted-foreground text-sm">
                            15 projects
                          </span>
                        </div>
                        <div className="mb-4">
                          <div className="mb-2 flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              Proficiency
                            </span>
                            <span className="text-secondary font-semibold">
                              80%
                            </span>
                          </div>
                          <div className="bg-muted h-2 w-full rounded-full">
                            <motion.div
                              className="h-2 rounded-full bg-gradient-to-r from-orange-500 to-yellow-500"
                              style={{ width: '80%' }}
                              initial={{ width: 0 }}
                              animate={{ width: '80%' }}
                              transition={{
                                duration: 1.5,
                                delay: 0.8,
                                ease: 'easeOut',
                              }}
                            ></motion.div>
                          </div>
                        </div>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                          Full-stack app deployment platform
                        </p>
                        <div className="absolute top-4 right-4 h-2 w-2 rounded-full bg-gradient-to-r from-orange-500 to-yellow-500 opacity-50 transition-opacity group-hover:opacity-100"></div>
                      </motion.div>
                      <motion.div
                        variants={cardVariants}
                        className="bg-card/80 border-secondary/20 group hover:border-primary/30 relative rounded-xl border p-6 transition-all duration-300 hover:shadow-lg"
                        whileHover={{
                          transition: { duration: 0.3, ease: 'easeOut' },
                        }}
                      >
                        <div className="mb-4 flex items-center justify-between">
                          <div className="text-foreground group-hover:text-primary text-lg font-bold transition-colors">
                            Supabase
                          </div>
                          <span className="text-muted-foreground text-sm">
                            18 projects
                          </span>
                        </div>
                        <div className="mb-4">
                          <div className="mb-2 flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              Proficiency
                            </span>
                            <span className="text-secondary font-semibold">
                              82%
                            </span>
                          </div>
                          <div className="bg-muted h-2 w-full rounded-full">
                            <motion.div
                              className="h-2 rounded-full bg-gradient-to-r from-orange-500 to-yellow-500"
                              style={{ width: '82%' }}
                              initial={{ width: 0 }}
                              animate={{ width: '82%' }}
                              transition={{
                                duration: 1.5,
                                delay: 1.0,
                                ease: 'easeOut',
                              }}
                            ></motion.div>
                          </div>
                        </div>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                          Open-source Firebase alternative
                        </p>
                        <div className="absolute top-4 right-4 h-2 w-2 rounded-full bg-gradient-to-r from-orange-500 to-yellow-500 opacity-50 transition-opacity group-hover:opacity-100"></div>
                      </motion.div>
                      <motion.div
                        variants={cardVariants}
                        className="bg-card/80 border-secondary/20 group hover:border-primary/30 relative rounded-xl border p-6 transition-all duration-300 hover:shadow-lg"
                        whileHover={{
                          transition: { duration: 0.3, ease: 'easeOut' },
                        }}
                      >
                        <div className="mb-4 flex items-center justify-between">
                          <div className="text-foreground group-hover:text-primary text-lg font-bold transition-colors">
                            PlanetScale
                          </div>
                          <span className="text-muted-foreground text-sm">
                            12 projects
                          </span>
                        </div>
                        <div className="mb-4">
                          <div className="mb-2 flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              Proficiency
                            </span>
                            <span className="text-secondary font-semibold">
                              78%
                            </span>
                          </div>
                          <div className="bg-muted h-2 w-full rounded-full">
                            <motion.div
                              className="h-2 rounded-full bg-gradient-to-r from-orange-500 to-yellow-500"
                              style={{ width: '78%' }}
                              initial={{ width: 0 }}
                              animate={{ width: '78%' }}
                              transition={{
                                duration: 1.5,
                                delay: 1.2,
                                ease: 'easeOut',
                              }}
                            ></motion.div>
                          </div>
                        </div>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                          Serverless MySQL platform
                        </p>
                        <div className="absolute top-4 right-4 h-2 w-2 rounded-full bg-gradient-to-r from-orange-500 to-yellow-500 opacity-50 transition-opacity group-hover:opacity-100"></div>
                      </motion.div>
                    </>
                  )}

                  {activeTechStack === 'tools' && (
                    <>
                      <motion.div
                        variants={cardVariants}
                        className="bg-card/80 border-secondary/20 group hover:border-primary/30 relative rounded-xl border p-6 transition-all duration-300 hover:shadow-lg"
                        whileHover={{
                          transition: { duration: 0.3, ease: 'easeOut' },
                        }}
                      >
                        <div className="mb-4 flex items-center justify-between">
                          <div className="text-foreground group-hover:text-primary text-lg font-bold transition-colors">
                            Git & GitHub
                          </div>
                          <span className="text-muted-foreground text-sm">
                            50+ projects
                          </span>
                        </div>
                        <div className="mb-4">
                          <div className="mb-2 flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              Proficiency
                            </span>
                            <span className="text-secondary font-semibold">
                              95%
                            </span>
                          </div>
                          <div className="bg-muted h-2 w-full rounded-full">
                            <motion.div
                              className="h-2 rounded-full bg-gradient-to-r from-purple-500 to-pink-500"
                              style={{ width: '95%' }}
                              initial={{ width: 0 }}
                              animate={{ width: '95%' }}
                              transition={{
                                duration: 1.5,
                                delay: 0.2,
                                ease: 'easeOut',
                              }}
                            ></motion.div>
                          </div>
                        </div>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                          Version control and collaborative development
                        </p>
                        <div className="absolute top-4 right-4 h-2 w-2 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 opacity-50 transition-opacity group-hover:opacity-100"></div>
                      </motion.div>
                      <motion.div
                        variants={cardVariants}
                        className="bg-card/80 border-secondary/20 group hover:border-primary/30 relative rounded-xl border p-6 transition-all duration-300 hover:shadow-lg"
                        whileHover={{
                          transition: { duration: 0.3, ease: 'easeOut' },
                        }}
                      >
                        <div className="mb-4 flex items-center justify-between">
                          <div className="text-foreground group-hover:text-primary text-lg font-bold transition-colors">
                            VS Code
                          </div>
                          <span className="text-muted-foreground text-sm">
                            Daily use
                          </span>
                        </div>
                        <div className="mb-4">
                          <div className="mb-2 flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              Proficiency
                            </span>
                            <span className="text-secondary font-semibold">
                              98%
                            </span>
                          </div>
                          <div className="bg-muted h-2 w-full rounded-full">
                            <motion.div
                              className="h-2 rounded-full bg-gradient-to-r from-purple-500 to-pink-500"
                              style={{ width: '98%' }}
                              initial={{ width: 0 }}
                              animate={{ width: '98%' }}
                              transition={{
                                duration: 1.5,
                                delay: 0.4,
                                ease: 'easeOut',
                              }}
                            ></motion.div>
                          </div>
                        </div>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                          Advanced code editor with extensive extensions
                        </p>
                        <div className="absolute top-4 right-4 h-2 w-2 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 opacity-50 transition-opacity group-hover:opacity-100"></div>
                      </motion.div>
                      <motion.div
                        variants={cardVariants}
                        className="bg-card/80 border-secondary/20 group hover:border-primary/30 relative rounded-xl border p-6 transition-all duration-300 hover:shadow-lg"
                        whileHover={{
                          transition: { duration: 0.3, ease: 'easeOut' },
                        }}
                      >
                        <div className="mb-4 flex items-center justify-between">
                          <div className="text-foreground group-hover:text-primary text-lg font-bold transition-colors">
                            Jest & Testing
                          </div>
                          <span className="text-muted-foreground text-sm">
                            35 projects
                          </span>
                        </div>
                        <div className="mb-4">
                          <div className="mb-2 flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              Proficiency
                            </span>
                            <span className="text-secondary font-semibold">
                              88%
                            </span>
                          </div>
                          <div className="bg-muted h-2 w-full rounded-full">
                            <motion.div
                              className="h-2 rounded-full bg-gradient-to-r from-purple-500 to-pink-500"
                              style={{ width: '88%' }}
                              initial={{ width: 0 }}
                              animate={{ width: '88%' }}
                              transition={{
                                duration: 1.5,
                                delay: 0.6,
                                ease: 'easeOut',
                              }}
                            ></motion.div>
                          </div>
                        </div>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                          Unit testing and test-driven development
                        </p>
                        <div className="absolute top-4 right-4 h-2 w-2 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 opacity-50 transition-opacity group-hover:opacity-100"></div>
                      </motion.div>
                      <motion.div
                        variants={cardVariants}
                        className="bg-card/80 border-secondary/20 group hover:border-primary/30 relative rounded-xl border p-6 transition-all duration-300 hover:shadow-lg"
                        whileHover={{
                          transition: { duration: 0.3, ease: 'easeOut' },
                        }}
                      >
                        <div className="mb-4 flex items-center justify-between">
                          <div className="text-foreground group-hover:text-primary text-lg font-bold transition-colors">
                            Cypress
                          </div>
                          <span className="text-muted-foreground text-sm">
                            25 projects
                          </span>
                        </div>
                        <div className="mb-4">
                          <div className="mb-2 flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              Proficiency
                            </span>
                            <span className="text-secondary font-semibold">
                              85%
                            </span>
                          </div>
                          <div className="bg-muted h-2 w-full rounded-full">
                            <motion.div
                              className="h-2 rounded-full bg-gradient-to-r from-purple-500 to-pink-500"
                              style={{ width: '85%' }}
                              initial={{ width: 0 }}
                              animate={{ width: '85%' }}
                              transition={{
                                duration: 1.5,
                                delay: 0.8,
                                ease: 'easeOut',
                              }}
                            ></motion.div>
                          </div>
                        </div>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                          End-to-end testing for web applications
                        </p>
                        <div className="absolute top-4 right-4 h-2 w-2 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 opacity-50 transition-opacity group-hover:opacity-100"></div>
                      </motion.div>
                      <motion.div
                        variants={cardVariants}
                        className="bg-card/80 border-secondary/20 group hover:border-primary/30 relative rounded-xl border p-6 transition-all duration-300 hover:shadow-lg"
                        whileHover={{
                          transition: { duration: 0.3, ease: 'easeOut' },
                        }}
                      >
                        <div className="mb-4 flex items-center justify-between">
                          <div className="text-foreground group-hover:text-primary text-lg font-bold transition-colors">
                            N8N
                          </div>
                          <span className="text-muted-foreground text-sm">
                            20 projects
                          </span>
                        </div>
                        <div className="mb-4">
                          <div className="mb-2 flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              Proficiency
                            </span>
                            <span className="text-secondary font-semibold">
                              82%
                            </span>
                          </div>
                          <div className="bg-muted h-2 w-full rounded-full">
                            <motion.div
                              className="h-2 rounded-full bg-gradient-to-r from-purple-500 to-pink-500"
                              style={{ width: '82%' }}
                              initial={{ width: 0 }}
                              animate={{ width: '82%' }}
                              transition={{
                                duration: 1.5,
                                delay: 1.0,
                                ease: 'easeOut',
                              }}
                            ></motion.div>
                          </div>
                        </div>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                          Workflow automation and API integrations
                        </p>
                        <div className="absolute top-4 right-4 h-2 w-2 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 opacity-50 transition-opacity group-hover:opacity-100"></div>
                      </motion.div>
                      <motion.div
                        variants={cardVariants}
                        className="bg-card/80 border-secondary/20 group hover:border-primary/30 relative rounded-xl border p-6 transition-all duration-300 hover:shadow-lg"
                        whileHover={{
                          transition: { duration: 0.3, ease: 'easeOut' },
                        }}
                      >
                        <div className="mb-4 flex items-center justify-between">
                          <div className="text-foreground group-hover:text-primary text-lg font-bold transition-colors">
                            Figma
                          </div>
                          <span className="text-muted-foreground text-sm">
                            15 projects
                          </span>
                        </div>
                        <div className="mb-4">
                          <div className="mb-2 flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              Proficiency
                            </span>
                            <span className="text-secondary font-semibold">
                              80%
                            </span>
                          </div>
                          <div className="bg-muted h-2 w-full rounded-full">
                            <motion.div
                              className="h-2 rounded-full bg-gradient-to-r from-purple-500 to-pink-500"
                              style={{ width: '80%' }}
                              initial={{ width: 0 }}
                              animate={{ width: '80%' }}
                              transition={{
                                duration: 1.5,
                                delay: 1.2,
                                ease: 'easeOut',
                              }}
                            ></motion.div>
                          </div>
                        </div>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                          UI/UX design and prototyping
                        </p>
                        <div className="absolute top-4 right-4 h-2 w-2 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 opacity-50 transition-opacity group-hover:opacity-100"></div>
                      </motion.div>
                    </>
                  )}

                  {activeTechStack === 'web3' && (
                    <>
                      <motion.div
                        variants={cardVariants}
                        className="bg-card/80 border-secondary/20 group hover:border-primary/30 relative rounded-xl border p-6 transition-all duration-300 hover:shadow-lg"
                        whileHover={{
                          transition: { duration: 0.3, ease: 'easeOut' },
                        }}
                      >
                        <div className="mb-4 flex items-center justify-between">
                          <div className="text-foreground group-hover:text-primary text-lg font-bold transition-colors">
                            Solidity
                          </div>
                          <span className="text-muted-foreground text-sm">
                            15 projects
                          </span>
                        </div>
                        <div className="mb-4">
                          <div className="mb-2 flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              Proficiency
                            </span>
                            <span className="text-secondary font-semibold">
                              80%
                            </span>
                          </div>
                          <div className="bg-muted h-2 w-full rounded-full">
                            <motion.div
                              className="h-2 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500"
                              style={{ width: '80%' }}
                              initial={{ width: 0 }}
                              animate={{ width: '80%' }}
                              transition={{
                                duration: 1.5,
                                delay: 0.2,
                                ease: 'easeOut',
                              }}
                            ></motion.div>
                          </div>
                        </div>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                          Smart contract development for Ethereum
                        </p>
                        <div className="absolute top-4 right-4 h-2 w-2 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 opacity-50 transition-opacity group-hover:opacity-100"></div>
                      </motion.div>
                      <motion.div
                        variants={cardVariants}
                        className="bg-card/80 border-secondary/20 group hover:border-primary/30 relative rounded-xl border p-6 transition-all duration-300 hover:shadow-lg"
                        whileHover={{
                          transition: { duration: 0.3, ease: 'easeOut' },
                        }}
                      >
                        <div className="mb-4 flex items-center justify-between">
                          <div className="text-foreground group-hover:text-primary text-lg font-bold transition-colors">
                            Web3.js
                          </div>
                          <span className="text-muted-foreground text-sm">
                            12 projects
                          </span>
                        </div>
                        <div className="mb-4">
                          <div className="mb-2 flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              Proficiency
                            </span>
                            <span className="text-secondary font-semibold">
                              75%
                            </span>
                          </div>
                          <div className="bg-muted h-2 w-full rounded-full">
                            <motion.div
                              className="h-2 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500"
                              style={{ width: '75%' }}
                              initial={{ width: 0 }}
                              animate={{ width: '75%' }}
                              transition={{
                                duration: 1.5,
                                delay: 0.4,
                                ease: 'easeOut',
                              }}
                            ></motion.div>
                          </div>
                        </div>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                          Ethereum JavaScript API integration
                        </p>
                        <div className="absolute top-4 right-4 h-2 w-2 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 opacity-50 transition-opacity group-hover:opacity-100"></div>
                      </motion.div>
                      <motion.div
                        variants={cardVariants}
                        className="bg-card/80 border-secondary/20 group hover:border-primary/30 relative rounded-xl border p-6 transition-all duration-300 hover:shadow-lg"
                        whileHover={{
                          transition: { duration: 0.3, ease: 'easeOut' },
                        }}
                      >
                        <div className="mb-4 flex items-center justify-between">
                          <div className="text-foreground group-hover:text-primary text-lg font-bold transition-colors">
                            Hardhat
                          </div>
                          <span className="text-muted-foreground text-sm">
                            10 projects
                          </span>
                        </div>
                        <div className="mb-4">
                          <div className="mb-2 flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              Proficiency
                            </span>
                            <span className="text-secondary font-semibold">
                              70%
                            </span>
                          </div>
                          <div className="bg-muted h-2 w-full rounded-full">
                            <motion.div
                              className="h-2 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500"
                              style={{ width: '70%' }}
                              initial={{ width: 0 }}
                              animate={{ width: '70%' }}
                              transition={{
                                duration: 1.5,
                                delay: 0.6,
                                ease: 'easeOut',
                              }}
                            ></motion.div>
                          </div>
                        </div>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                          Ethereum development environment
                        </p>
                        <div className="absolute top-4 right-4 h-2 w-2 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 opacity-50 transition-opacity group-hover:opacity-100"></div>
                      </motion.div>
                      <motion.div
                        variants={cardVariants}
                        className="bg-card/80 border-secondary/20 group hover:border-primary/30 relative rounded-xl border p-6 transition-all duration-300 hover:shadow-lg"
                        whileHover={{
                          transition: { duration: 0.3, ease: 'easeOut' },
                        }}
                      >
                        <div className="mb-4 flex items-center justify-between">
                          <div className="text-foreground group-hover:text-primary text-lg font-bold transition-colors">
                            IPFS
                          </div>
                          <span className="text-muted-foreground text-sm">
                            8 projects
                          </span>
                        </div>
                        <div className="mb-4">
                          <div className="mb-2 flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              Proficiency
                            </span>
                            <span className="text-secondary font-semibold">
                              65%
                            </span>
                          </div>
                          <div className="bg-muted h-2 w-full rounded-full">
                            <motion.div
                              className="h-2 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500"
                              style={{ width: '65%' }}
                              initial={{ width: 0 }}
                              animate={{ width: '65%' }}
                              transition={{
                                duration: 1.5,
                                delay: 0.8,
                                ease: 'easeOut',
                              }}
                            ></motion.div>
                          </div>
                        </div>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                          Decentralized file storage system
                        </p>
                        <div className="absolute top-4 right-4 h-2 w-2 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 opacity-50 transition-opacity group-hover:opacity-100"></div>
                      </motion.div>
                      <motion.div
                        variants={cardVariants}
                        className="bg-card/80 border-secondary/20 group hover:border-primary/30 relative rounded-xl border p-6 transition-all duration-300 hover:shadow-lg"
                        whileHover={{
                          transition: { duration: 0.3, ease: 'easeOut' },
                        }}
                      >
                        <div className="mb-4 flex items-center justify-between">
                          <div className="text-foreground group-hover:text-primary text-lg font-bold transition-colors">
                            MetaMask
                          </div>
                          <span className="text-muted-foreground text-sm">
                            15 projects
                          </span>
                        </div>
                        <div className="mb-4">
                          <div className="mb-2 flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              Proficiency
                            </span>
                            <span className="text-secondary font-semibold">
                              78%
                            </span>
                          </div>
                          <div className="bg-muted h-2 w-full rounded-full">
                            <motion.div
                              className="h-2 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500"
                              style={{ width: '78%' }}
                              initial={{ width: 0 }}
                              animate={{ width: '78%' }}
                              transition={{
                                duration: 1.5,
                                delay: 1.0,
                                ease: 'easeOut',
                              }}
                            ></motion.div>
                          </div>
                        </div>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                          Wallet integration and user authentication
                        </p>
                        <div className="absolute top-4 right-4 h-2 w-2 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 opacity-50 transition-opacity group-hover:opacity-100"></div>
                      </motion.div>
                      <motion.div
                        variants={cardVariants}
                        className="bg-card/80 border-secondary/20 group hover:border-primary/30 relative rounded-xl border p-6 transition-all duration-300 hover:shadow-lg"
                        whileHover={{
                          transition: { duration: 0.3, ease: 'easeOut' },
                        }}
                      >
                        <div className="mb-4 flex items-center justify-between">
                          <div className="text-foreground group-hover:text-primary text-lg font-bold transition-colors">
                            Polygon
                          </div>
                          <span className="text-muted-foreground text-sm">
                            6 projects
                          </span>
                        </div>
                        <div className="mb-4">
                          <div className="mb-2 flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              Proficiency
                            </span>
                            <span className="text-secondary font-semibold">
                              60%
                            </span>
                          </div>
                          <div className="bg-muted h-2 w-full rounded-full">
                            <motion.div
                              className="h-2 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500"
                              style={{ width: '60%' }}
                              initial={{ width: 0 }}
                              animate={{ width: '60%' }}
                              transition={{
                                duration: 1.5,
                                delay: 1.2,
                                ease: 'easeOut',
                              }}
                            ></motion.div>
                          </div>
                        </div>
                        <p className="text-muted-foreground text-sm leading-relaxed">
                          Layer 2 scaling solution for Ethereum
                        </p>
                        <div className="absolute top-4 right-4 h-2 w-2 rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 opacity-50 transition-opacity group-hover:opacity-100"></div>
                      </motion.div>
                    </>
                  )}
                </motion.div>
              </div>
            </motion.section>
          </div>
        </div>
      </div>
    </div>
  );
}
